import { useEffect, useRef } from 'react';
import { useSound } from './SoundProvider';

function SpeakerIcon({ muted = false }: { muted?: boolean }) {
  return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 10v4h4l5 4V6l-5 4H5Z" />
    {muted ? <><path d="m18 9 4 4" /><path d="m22 9-4 4" /></> : <><path d="M17 9.5a4 4 0 0 1 0 5" /><path d="M19.5 7a7.5 7.5 0 0 1 0 10" /></>}
  </svg>;
}

export function SoundSettingsPanel() {
  const { preferences, play, setEnabled, setQuietMode, setVolume, setHapticsEnabled, setReducedMotion } = useSound();
  const status = !preferences.enabled ? '효과음 꺼짐' : preferences.quietMode ? '조용히 사용 중' : '효과음 켜짐';
  return <section className="sound-settings-panel" aria-label="소리와 액션 설정">
    <header><div><small>MRM 반응 설정</small><strong>{status}</strong></div><SpeakerIcon muted={!preferences.enabled} /></header>
    <label className="sound-toggle-row"><span><strong>효과음</strong><small>선택·저장·성장 순간을 짧은 소리로 알려요</small></span><input type="checkbox" checked={preferences.enabled} onChange={(event) => setEnabled(event.target.checked)} /></label>
    <label className="sound-volume-row"><span><strong>볼륨</strong><b>{Math.round(preferences.volume * 100)}%</b></span><input type="range" min="0" max="1" step="0.05" value={preferences.volume} disabled={!preferences.enabled} onChange={(event) => setVolume(Number(event.target.value))} /></label>
    <label className="sound-toggle-row"><span><strong>조용히 사용하기</strong><small>버튼음은 끄고 중요한 완료음만 작게 재생해요</small></span><input type="checkbox" checked={preferences.quietMode} disabled={!preferences.enabled} onChange={(event) => setQuietMode(event.target.checked)} /></label>
    <label className="sound-toggle-row"><span><strong>햅틱 반응</strong><small>지원 기기에서 중요한 순간을 짧은 진동으로 알려요</small></span><input type="checkbox" checked={preferences.hapticsEnabled} onChange={(event) => setHapticsEnabled(event.target.checked)} /></label>
    <label className="sound-toggle-row"><span><strong>저자극 모션</strong><small>캐릭터 이동과 반짝임을 부드러운 전환으로 바꿔요</small></span><input type="checkbox" checked={preferences.reducedMotion} onChange={(event) => setReducedMotion(event.target.checked)} /></label>
    <button type="button" className="sound-preview-button" data-sound="none" disabled={!preferences.enabled} onClick={() => void play('badge-unlock')}>배지 소리 미리듣기</button>
    <p>첫 터치 이후에만 재생됩니다. 소리 없이도 모든 결과를 화면에서 확인할 수 있어요.</p>
  </section>;
}

export function SoundSettingsControl({ withBottomNavigation = false, desktopOnly = false }: { withBottomNavigation?: boolean; desktopOnly?: boolean }) {
  const { preferences } = useSound();
  const status = !preferences.enabled ? '효과음 꺼짐' : preferences.quietMode ? '조용히 사용 중' : '효과음 켜짐';
  return <details className={`sound-settings-control ${withBottomNavigation ? 'above-bottom-navigation' : ''} ${desktopOnly ? 'desktop-sound-control' : ''} ${preferences.enabled ? 'sound-on' : 'sound-off'}`}>
    <summary aria-label={`멀티미디어 설정 · ${status}`} title="멀티미디어 설정" data-sound="none">
      <SpeakerIcon muted={!preferences.enabled} />
      <span className="sound-status-dot" aria-hidden="true" />
    </summary>
    <SoundSettingsPanel />
  </details>;
}

export function SoundNavigationControl() {
  const { preferences } = useSound();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const status = !preferences.enabled ? '효과음 꺼짐' : preferences.quietMode ? '조용히 사용 중' : '효과음 켜짐';

  useEffect(() => {
    const closeOutside = (event: PointerEvent) => {
      const details = detailsRef.current;
      if (details?.open && event.target instanceof Node && !details.contains(event.target)) details.open = false;
    };
    const closeEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && detailsRef.current?.open) {
        detailsRef.current.open = false;
        detailsRef.current.querySelector<HTMLElement>('summary')?.focus();
      }
    };
    document.addEventListener('pointerdown', closeOutside);
    window.addEventListener('keydown', closeEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      window.removeEventListener('keydown', closeEscape);
    };
  }, []);

  return <details ref={detailsRef} className={`bottom-nav-sound ${preferences.enabled ? 'sound-on' : 'sound-off'}`}>
    <summary aria-label={`소리와 액션 설정 · ${status}`} title="소리와 액션 설정" data-sound="none">
      <span className="navigation-icon sound-navigation-icon"><SpeakerIcon muted={!preferences.enabled} /><span className="sound-status-dot" aria-hidden="true" /></span>
      <span className="navigation-label">소리</span>
    </summary>
    <SoundSettingsPanel />
  </details>;
}
